import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import CopyIcon from "@mui/icons-material/FileCopyOutlined";
import LinkIcon from "@mui/icons-material/Link";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import Loader from "_shared/_views/loading/loader";
import LoadingButton from "_shared/_views/loading/loading-button";
import Guid from "_shared/guids/guid";
import * as Config from "config";
import {
  getOpenEntityMemberInvitationAPIErrorData,
  useCreateOpenMemberInvitationMutation,
  useDeleteOpenMemberInvitationMutation,
  useLazyGetAllOpenEntityMemberInvitationsQuery,
} from "legal-entities/invitations/member-invitations/open/_api/open-entity-member-invitation-api-service";
import OpenEntityMemberInvitation from "legal-entities/invitations/member-invitations/open/open-entity-member-invitation";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSession } from "users/__session/session-context";
import { TimestampCell } from "_shared/_views/tables/date-cells";

const OpenInvitationLinks = styled(Table)(() => ({
  "& td": {
    "&:first-of-type": {
      paddingLeft: 0,
    },
    "&:last-of-type": {
      paddingRight: 0,
    },
    borderBottom: "none",
  },
}));
const CreateInvitationLink = styled(LoadingButton)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

type OpenInvitationLinkSectionProps = {
  className?: string;
};

/**
 * A link anyone can follow to join the organization, for the people a leader cannot or would rather
 * not name in advance.
 *
 * It used to share a dialog with a form inviting one named person. The colleague roster does that
 * and more (a title, Marketplace publication, the client the invitation names), so the form went
 * and this is what was left. It now sits under the roster, where it is one of two ways of inviting
 * people rather than one of two buttons.
 */
export default function OpenInvitationLinkSection(props: Readonly<OpenInvitationLinkSectionProps>) {
  const { className } = props;

  const session = useSession();
  const confirmDialog = useConfirmDialog();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [focusedOpenInvitation, setFocusedOpenInvitation] = useState<OpenEntityMemberInvitation | null>(null);
  const [focusedOpenInvitationURL, setFocusedOpenInvitationURL] = useState<URL | null>(null);
  const [creating, setCreating] = useState<boolean>(false);
  const loadingInvitationsRef = useRef(false);

  const [getAllOpenEntityMemberInvitations] = useLazyGetAllOpenEntityMemberInvitationsQuery();
  const [createOpenMemberInvitation] = useCreateOpenMemberInvitationMutation();
  const [deleteOpenMemberInvitation] = useDeleteOpenMemberInvitationMutation();

  const buildInvitationURL = (invitationId?: Guid): URL | null => {
    if (!invitationId) return null;
    return new URL(`${Config.entityMemberInvitationLink}/${invitationId.value}`);
  };

  const loadInvitations = useCallback(async () => {
    if (loadingInvitationsRef.current) return;
    loadingInvitationsRef.current = true;
    setIsLoading(true);
    // The cleanup follows the try rather than sitting in a finally, which the React Compiler cannot
    // lower. Nothing in the try returns early, so it always runs. See app/CLAUDE.md, React Compiler.
    try {
      const openInvitations = await getAllOpenEntityMemberInvitations({
        entityId: session.user!.legalEntityId,
      }).unwrap();
      // The API leaves out expired links and lists the rest newest first, so the first is the
      // organization's current link.
      if (openInvitations.length > 0) {
        setFocusedOpenInvitation(openInvitations[0]);
        setFocusedOpenInvitationURL(buildInvitationURL(openInvitations[0].id));
      }
    } catch (ex) {
      console.error(ex);
      enqueueSnackbar("Failed to get invitations", { variant: "error" });
    }
    loadingInvitationsRef.current = false;
    setIsLoading(false);
  }, [getAllOpenEntityMemberInvitations, session.user]);

  useEffect(() => {
    let active = true;
    queueMicrotask(() => {
      if (!active) return;
      void loadInvitations();
    });
    return () => {
      active = false;
    };
  }, [loadInvitations]);

  const createInvitationLink = async (entityId: Guid) => {
    setCreating(true);
    try {
      const createdInvitation = await createOpenMemberInvitation({ entityId }).unwrap();

      setFocusedOpenInvitation(createdInvitation);
      setFocusedOpenInvitationURL(buildInvitationURL(createdInvitation.id));

      await loadInvitations();

      enqueueSnackbar("Created new invitation link", { variant: "success" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Couldn't get invitation link. Please try again.", { variant: "error" });
    }
    setCreating(false);
  };

  const deleteInvitationLink = async (invitation: OpenEntityMemberInvitation) => {
    try {
      const confirmResponse = await confirmDialog({
        title: "Delete Invitation",
        message: "Are you sure you want to delete this invitation?",
        okButtonText: "Delete",
        cancelButtonText: "Cancel",
      });

      if (confirmResponse === ConfirmResponse.Cancel) return;

      await deleteOpenMemberInvitation({ invitation }).unwrap();

      setFocusedOpenInvitation(null);

      enqueueSnackbar("Deleted invitation link", { variant: "success" });
    } catch (error) {
      console.error(error);
      const apiErrorData = getOpenEntityMemberInvitationAPIErrorData(error);
      if (apiErrorData) {
        enqueueSnackbar(String(apiErrorData), { variant: "error" });
        return;
      }
      enqueueSnackbar("Failed to delete invitation", { variant: "error" });
    }
  };

  const copyToClipboard = async (textToCopy?: string) => {
    if (!textToCopy) {
      enqueueSnackbar("No link to copy", { variant: "warning" });
      return;
    }
    await navigator.clipboard.writeText(textToCopy);
    enqueueSnackbar("Copied link to clipboard", { variant: "info" });
  };

  return (
    <section aria-labelledby="open-invitation-link-heading" className={className}>
      <Typography id="open-invitation-link-heading" variant="h6">
        Or share a link
      </Typography>
      <Typography color="text.secondary" variant="body2">
        Anyone with the link can join your organization, so nobody has to be named in advance.
      </Typography>
      {focusedOpenInvitation !== null && (
        <OpenInvitationLinks>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell>
                  <Loader />
                </TableCell>
              </TableRow>
            )}
            <TableRow key={focusedOpenInvitation.id?.value}>
              <TableCell align="left">
                <Typography variant={"subtitle2"}>{focusedOpenInvitationURL?.toString()}</Typography>
              </TableCell>
              <TableCell align="left">
                <Typography variant={"body2"}>
                  Expires <TimestampCell value={focusedOpenInvitation.expires} />
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Button
                  endIcon={<CopyIcon />}
                  color="primary"
                  onClick={() => copyToClipboard(focusedOpenInvitationURL?.toString())}
                >
                  Copy
                </Button>
                <Button
                  endIcon={<DeleteIcon />}
                  color="error"
                  onClick={() => deleteInvitationLink(focusedOpenInvitation)}
                >
                  Delete
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </OpenInvitationLinks>
      )}
      <CreateInvitationLink
        startIcon={<LinkIcon />}
        variant="outlined"
        color="primary"
        loading={creating}
        disabled={focusedOpenInvitation !== null}
        onClick={() => createInvitationLink(session.user!.legalEntityId)}
      >
        Create Invite Link
      </CreateInvitationLink>
    </section>
  );
}
